// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@vanmeter/App/Components/Notify";
import { xIsAuthenticated, xIsGuest } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import { parseBoolean } from "@vanmeter/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    const isAuthenticated = xIsAuthenticated(state);
    const currentMode = getCookie(NavigationModeCookieName);
    const isGuest = xIsGuest(state);

    return {
        cart,
        // XNG-Changes: get errors from cartLines and cartHeader widgets
        productsCannotBePurchased: cart?.cartLines?.some(o => o.isRestricted || !o.isActive || o.cantBuy),
        cartNotPriced: cart?.cartNotPriced,
        hasInsufficientInventory: cart?.hasInsufficientInventory && cart?.fulfillmentMethod === "Ship",
        hasInsufficientInventoryAtPickUp: cart?.hasInsufficientInventory && cart?.fulfillmentMethod === "PickUp",
        hasInvalidPrice: hasProductsWithInvalidPrice(cart),
        failedToGetRealTimeInventory: cart?.failedToGetRealTimeInventory,
        shouldShowVmiMessage: isAuthenticated && currentMode === "Vmi",
        shouldShowApprovalEditMessage: isAuthenticated && cart?.status === "AwaitingApprovalEditDraft",
        isCreditHold: parseBoolean(
            getBillToState(state, state.context.session.billToId).value?.properties?.isCreditHold,
        ),
        isEstimator: parseBoolean(getSession(state).properties.isUserInEstimatorRole),
        hasQuoteRequired: (cart?.quoteRequiredCount ?? 0) > 0,
        hasOutOfStockAsGuest: (!isAuthenticated || isGuest) && cart?.cartLines?.some(l => l.qtyOnHand === 0),
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
    // XNG-Changes
    notify?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({
    cart,
    // XNG-changes
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInsufficientInventoryAtPickUp,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    shouldShowVmiMessage,
    shouldShowApprovalEditMessage,
    isCreditHold,
    isEstimator,
    hasQuoteRequired,
    hasOutOfStockAsGuest,
}: Props) => {
    // XNG-changes: remove !cart.isAwaitingApproval
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    const hasErrors =
        productsCannotBePurchased ||
        cartNotPriced ||
        hasInsufficientInventory ||
        hasInsufficientInventoryAtPickUp ||
        failedToGetRealTimeInventory ||
        (!cartNotPriced && hasInvalidPrice) ||
        isCreditHold ||
        isEstimator ||
        shouldShowVmiMessage ||
        shouldShowApprovalEditMessage ||
        hasQuoteRequired ||
        cart.isAwaitingApproval ||
        hasOutOfStockAsGuest;

    if (!hasErrors) {
        return null;
    }

    // XNG-changes: use notify and add messages from cartLines and cartHeader widget
    return (
        <StyledWrapper {...styles.approvingWrapper}>
            {cart.isAwaitingApproval && (
                <Notify {...styles.notify} type="info" data-test-selector="cart_approvingOrderMessage">
                    {translate("You are approving order #{0}", cart.orderNumber)}
                </Notify>
            )}
            {shouldShowApprovalEditMessage && (
                <Notify {...styles.notify} type="info" data-test-selector="cart_approvingOrderMessage">
                    {translate("You are editing order #{0}", cart.orderNumber.split("_")[0])}
                </Notify>
            )}
            {shouldShowVmiMessage && (
                <Notify {...styles.notify} type="info">
                    {siteMessage("Cart_View_On_Vmi")}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify {...styles.notify} type="warning">
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify {...styles.notify} type="danger">
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventory && (
                <Notify {...styles.notify} type="danger">
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventoryAtPickUp && (
                <Notify {...styles.notify} type="danger">
                    {siteMessage("Cart_InsufficientPickupInventory")}
                </Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify {...styles.notify} type="danger">
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify {...styles.notify} type="danger">
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
            {/* XNG-Changes: custom msgs */}
            {hasOutOfStockAsGuest && (
                <Notify {...styles.notify} type="warning">
                    {siteMessage("VM_Cart_GuestOutOfStockWarning")}
                </Notify>
            )}
            {isCreditHold && (
                <Notify {...styles.notify} type="warning">
                    {siteMessage("VM_Is_Credit_Hold_MSG")}
                </Notify>
            )}
            {isEstimator && (
                <Notify {...styles.notify} type="warning">
                    {siteMessage("VM_Estimator_User_Role_MSG")}
                </Notify>
            )}
            {hasQuoteRequired && (
                <Notify {...styles.notify} type="warning">
                    {siteMessage("Rfq_QuoteRequiredMsg")}
                </Notify>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
